// Backend lookups shared by the channel-selection dialogs (GraphChannelSelection,
// HistoricalTrend). Each falls back to the webserver REST path when not running
// under Tauri. Errors resolve to empty results so the dialogs degrade to an
// empty list rather than throwing.
import type { ChannelInfo, TurbineInfo } from "../types/dialog-types";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Channel store a list is drawn from. */
export type GraphChannelMode = "live" | "trend" | "event" | "multiTime";

const CHANNEL_LIST_CMD: Record<GraphChannelMode, string> = {
  live: "get_channel_list",
  trend: "get_trend_channel_list",
  event: "get_event_channel_list",
  multiTime: "get_channel_list",
};

export async function fetchTurbineList(): Promise<TurbineInfo[]> {
  try {
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      const json = await invoke<string>("get_config_list");
      return JSON.parse(json) as TurbineInfo[];
    }
    const response = await fetch("/api/get_turbine_list");
    const text = await response.text();
    if (!text) return [];
    const parsed = JSON.parse(text);
    // Backend returns a JSON string, so parsed may be a string or array.
    return typeof parsed === "string" ? JSON.parse(parsed) : parsed;
  } catch {
    return [];
  }
}

export async function fetchChannelList(
  configName: string,
  channelType: string,
  mode: GraphChannelMode,
): Promise<ChannelInfo[]> {
  const endpoint = CHANNEL_LIST_CMD[mode];
  try {
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      return await invoke<ChannelInfo[]>(endpoint, { configName, channelType });
    }
    const response = await fetch(
      `/api/${endpoint}?config_name=${encodeURIComponent(configName)}&channel_type=${encodeURIComponent(channelType)}`,
    );
    if (!response.ok) return [];
    return await response.json();
  } catch {
    return [];
  }
}

/** Event models configured for a turbine, as `[model_id, model_name]` pairs
 *  (`get_event_models`). */
export async function fetchEventModels(configName: string): Promise<Array<[string, string]>> {
  try {
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      return await invoke<Array<[string, string]>>("get_event_models", { configName });
    }
    const res = await fetch(`/api/get_event_models?config_name=${encodeURIComponent(configName)}`);
    if (!res.ok) return [];
    return (await res.json()) as Array<[string, string]>;
  } catch {
    return [];
  }
}

/** Per-config turbine state list (e.g. DEFAULT first), from
 *  `get_turbine_states` / `get_turbine_state_list`. */
export async function fetchTurbineStates(configName: string): Promise<string[]> {
  try {
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      return await invoke<string[]>("get_turbine_states", { configName });
    }
    const res = await fetch(`/api/get_turbine_states?config_name=${encodeURIComponent(configName)}`);
    const raw = (await res.json()) as unknown;
    return Array.isArray(raw) ? raw.map(String) : [];
  } catch {
    return [];
  }
}
